/**
 * OAuth Popup Handler
 *
 * Handles OAuth authentication in a popup window to avoid iframe restrictions.
 * Many OAuth providers block their login pages from being loaded in iframes.
 */

import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('web:auth:oauth-popup');

type OAuthResult = {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  user?: any;
  error?: string;
};

/**
 * Open OAuth flow in a popup window and wait for result
 */
export async function openOAuthPopup(authUrl: string): Promise<OAuthResult> {
  return new Promise(resolve => {
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Open popup window
    const popup = window.open(
      authUrl,
      'oauth_popup',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      logger.error('❌ [OAUTH-POPUP] Failed to open popup - blocked by browser');
      resolve({
        success: false,
        error: 'Popup blocked. Please allow popups for this site.',
      });
      return;
    }

    logger.info('✅ [OAUTH-POPUP] Popup opened successfully');

    // Listen for messages from the popup
    const messageHandler = (event: MessageEvent) => {
      // Verify origin (adjust for your domain in production)
      if (
        event.origin !== window.location.origin &&
        !event.origin.includes('localhost') &&
        !event.origin.includes('127.0.0.1')
      ) {
        logger.warn('⚠️ [OAUTH-POPUP] Received message from untrusted origin:', event.origin);
        return;
      }

      const data = event.data;

      // Check if this is an OAuth result message
      if (data.type === 'OAUTH_RESULT') {
        logger.info('✅ [OAUTH-POPUP] Received OAuth result');
        window.removeEventListener('message', messageHandler);
        popup.close();
        resolve(data.result);
      }
    };

    window.addEventListener('message', messageHandler);

    // Poll to detect if popup was closed manually
    const pollTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollTimer);
        window.removeEventListener('message', messageHandler);
        logger.warn('⚠️ [OAUTH-POPUP] Popup was closed by user');
        resolve({
          success: false,
          error: 'Authentication cancelled',
        });
      }
    }, 500);

    // Timeout after 5 minutes
    setTimeout(
      () => {
        if (!popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', messageHandler);
          popup.close();
          logger.error('❌ [OAUTH-POPUP] OAuth timeout');
          resolve({
            success: false,
            error: 'Authentication timeout',
          });
        }
      },
      5 * 60 * 1000
    );
  });
}

/**
 * Check if running inside iframe
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}
